
































































































import { Component, Vue } from "vue-property-decorator";
import MenuIconWithSelectionListComponent from "@/components/common/FilterMenu.vue";
import ContentStateComponent from "@/components/common/ContentState.vue";
import AdminRefreshButtonComponent from "@/components/admin/AdminRefreshButton.vue";
import { ExternalSystemRow } from "@/data/models/ClientPartner";
import { cachedExternalSystems, listExternalSystems } from "@/data/repository/ClientAdministrationRepository";
import { readThroughCache } from "@/utils/AdminReadCache";
import router from "@/router/router";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { COLOR_LIST_ROW_HOVER_DARK, COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { EXTERNAL_SYSTEM_FILTERS, ExternalSystemFilter, filterExternalSystems, partnerSystemLabelKey } from "@/utils/ClientPartners";
import { ComponentName } from "@/utils/RouteGuards";
import { getValue, setValue } from "@/utils/SecureLocalStorage";
import { themed } from "@/utils/Theme";
import { getLastVisitedItem, LAST_VISITED_ITEM_BACKGROUND_STYLE, setLastVisitedItem, StorageKeyLastVisitedItemLevel } from "@/utils/Utils";

/** Session storage keys (see `SecureLocalStorage`) for the list's filter and search. */
const FILTER_STORAGE_KEY = "ADMIN_EXTERNAL_SYSTEMS_FILTER";
const SEARCH_STORAGE_KEY = "ADMIN_EXTERNAL_SYSTEMS_SEARCH";
/** How many linked clients a row names before "und n weitere". */
const SUMMARY_CLIENTS = 2;

/**
 * **Kept alive, like the Mandanten list** (`includeKeepAlive` in `Scaffold.vue`, the explicit `name` below):
 * back from a system's page reuses this instance, shows the rows at once and scrolls the opened one back
 * into view, then reads the list again in the background (no loading state), so a status or link changed
 * on the page shows. Filter and search live in the component and session storage, never in the URL.
 *
 * **Not the access boundary**: `listExternalSystemsForAdministration` repeats the portal and
 * `MANAGE_CLIENTS` checks; the system's page and its changes check again.
 */
@Component({
  name: "ExternalSystemList",
  components: {
    "menu-icon": MenuIconWithSelectionListComponent,
    "content-state": ContentStateComponent,
    "admin-refresh-button": AdminRefreshButtonComponent,
  },
})
export default class ExternalSystemList extends Vue {
  systems: ExternalSystemRow[] = [];
  filter: ExternalSystemFilter = "ALL";
  search = "";
  isLoading = true;
  /** Showing the cached systems while IRES Cloud is asked again (`AdminReadCache.ts`). */
  isRefreshing = false;
  loadError = false;
  lastVisitedId: string | null = null;
  /** Gates the app-bar portal: a cached instance must not fill the bar of another screen. */
  componentActive = false;

  readonly filters = EXTERNAL_SYSTEM_FILTERS;

  get rows(): ExternalSystemRow[] {
    return filterExternalSystems(this.systems, this.filter, this.search);
  }

  get hasRows(): boolean {
    return !this.isLoading && !this.loadError && this.rows.length > 0;
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  systemLabelKey(system: string | null): string {
    return partnerSystemLabelKey(system);
  }

  linkedSummary(row: ExternalSystemRow): string {
    const shown = row.linkedClientNames.slice(0, SUMMARY_CLIENTS).join(", ");
    return row.linkedClientNames.length > SUMMARY_CLIENTS
      ? this.$t("admin.externalSystems.moreClients", [shown, row.linkedClientNames.length - SUMMARY_CLIENTS]).toString()
      : shown;
  }

  /** Hover wins over the last-opened tint, so the row under the pointer always responds. */
  rowStyle(row: ExternalSystemRow, hover: boolean): string {
    const background = hover ? themed("", COLOR_LIST_ROW_HOVER_DARK) : "";
    if (background) {
      return `background-color: ${background}`;
    }
    return row.id === this.lastVisitedId ? LAST_VISITED_ITEM_BACKGROUND_STYLE : "";
  }

  filterName(filter: ExternalSystemFilter): string {
    return filter === "ALL"
      ? this.$t("admin.externalSystems.filters.ALL").toString()
      : this.$t(`admin.clients.partners.statuses.${filter}`).toString();
  }

  onFilterChanged(filter: ExternalSystemFilter) {
    this.filter = filter;
    setValue(FILTER_STORAGE_KEY, filter);
  }

  onSearch(value: string | null) {
    this.search = value ?? "";
    setValue(SEARCH_STORAGE_KEY, this.search);
  }

  /** On its owner's page, where it is edited. */
  open(row: ExternalSystemRow) {
    this.lastVisitedId = row.id;
    setLastVisitedItem(row.id, StorageKeyLastVisitedItemLevel.LEVEL_1);
    // `from=list`: back on the system's page returns here, not to its owner.
    router.push({ name: ComponentName.CLIENT_ADMINISTRATION_PARTNER, query: { clientId: row.ownerClientId, partnerId: row.id, from: "list" } });
  }

  /** The cached systems at once when they were read before in this tab (here or on the cockpit), then IRES Cloud's. */
  async load() {
    const cached = cachedExternalSystems();
    this.isLoading = cached === null;
    this.isRefreshing = cached !== null;
    this.loadError = false;
    try {
      await readThroughCache(cached, listExternalSystems, (systems) => {
        this.systems = systems;
      });
    } catch {
      // Swallowed on purpose: the screen shows its own failure state, and the refresh button retries.
      this.loadError = true;
    } finally {
      this.isLoading = false;
      this.isRefreshing = false;
    }
    this.scrollToLastVisited();
  }

  /** Reads the list again without the loading state, so the rows on screen stay while it runs. */
  async reloadInBackground() {
    try {
      this.systems = await listExternalSystems();
      this.loadError = false;
    } catch {
      // Swallowed on purpose: the list keeps what it showed, and the refresh button reloads on demand.
    }
  }

  /** The row opened last back in view, as the Mandanten list does on return. */
  async scrollToLastVisited() {
    if (!this.lastVisitedId) {
      return;
    }
    await this.$nextTick();
    const row = Array.from(this.$el.querySelectorAll<HTMLElement>("[data-row-key]")).find(
      (element) => element.dataset.rowKey === this.lastVisitedId
    );
    row?.scrollIntoView({ block: "center" });
  }

  mounted() {
    this.componentActive = true;
    ToolbarContentModule.setI18nResource("admin.clients.partners.title");
    // A link may ask for a status (the IRES Admin cockpit's figures do); it is applied and stored. Read
    // once, here: later changes stay out of the URL, which keys this kept-alive screen.
    const linked = EXTERNAL_SYSTEM_FILTERS.find((filter) => filter === this.$route.query.filter);
    if (linked) {
      this.onFilterChanged(linked);
    } else {
      const stored = getValue(FILTER_STORAGE_KEY);
      this.filter = EXTERNAL_SYSTEM_FILTERS.find((filter) => filter === stored) ?? "ALL";
    }
    this.search = getValue(SEARCH_STORAGE_KEY) ?? "";
    this.lastVisitedId = getLastVisitedItem(StorageKeyLastVisitedItemLevel.LEVEL_1);
    this.load();
  }

  /** Back from the cache: rows at once, the opened one in view, then a quiet refresh. */
  activated() {
    this.componentActive = true;
    ToolbarContentModule.setI18nResource("admin.clients.partners.title");
    this.lastVisitedId = getLastVisitedItem(StorageKeyLastVisitedItemLevel.LEVEL_1);
    this.scrollToLastVisited();
    // Not on the first visit (activated follows mounted), where load() is still running.
    if (!this.isLoading && !this.isRefreshing) {
      this.reloadInBackground();
    }
  }

  deactivated() {
    this.componentActive = false;
  }
}
